import { LayoutGrid, LogOut, Menu, Palette, Sparkles, Store, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Wordmark } from "@/components/brand/Wordmark";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { openActivityCenter } from "@/lib/activityCenter";
import { NotificationsBell } from "@/components/NotificationsBell";
import { useLocalePath } from "@/lib/locale";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";

/*
 * Apple.com globalnav replica: 44px translucent blur bar, 12px type,
 * centered links, absolute simplicity.
 */

const NAV_LINKS = [
  { to: "/auctions", key: "nav.browseAuctions", testid: "nav-browse-auctions" },
  { to: "/for-organisers", key: "nav.forOrganisers", testid: "nav-for-organisers" },
  { to: "/features", key: "nav.features", testid: "nav-features" },
  { to: "/pricing", key: "nav.pricing", testid: "nav-pricing" },
  { to: "/how-it-works", key: "nav.howItWorks", testid: "nav-how-it-works" },
  { to: "/resources", key: "kb.title", testid: "nav-resources" },
  { to: "/about", key: "nav.about", testid: "nav-about" },
];

const navLinkClass = ({ isActive }) =>
  `text-xs tracking-[-0.01em] transition-colors duration-200 ${
    isActive ? "text-black" : "text-black/70 hover:text-black"
  }`;

const UserMenu = () => {
  const { user, logout } = useAuth();
  const { mode, setMode, canOrganize } = useMode();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const initials = user.name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    toast(t("auth.signedOutToast"));
    navigate("/");
  };

  /* B1: mode = which home you're in, never what you may do. */
  const switchMode = (m) => {
    setMode(m);
    navigate(m === "organizer" ? "/dashboard" : "/account");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" aria-label={t("nav.myAccount")} className="rounded-full">
        <Avatar className="h-7 w-7 border border-black/10">
          <AvatarFallback className="bg-[#0A0A0A] text-white text-[10px] font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl border-black/5 shadow-[0_16px_40px_rgb(0,0,0,0.08)] min-w-[14rem]">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="font-semibold">{user.name}</span>
          <span className="text-xs font-normal text-[#6e6e73]">{user.email}</span>
          <Badge
            data-testid="user-role-badge"
            className="w-fit mt-1 bg-[#1D4D3B]/10 text-[#1D4D3B] hover:bg-[#1D4D3B]/10 border-0 text-[10px] uppercase tracking-wider"
          >
            {user.role}
          </Badge>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canOrganize && (
          <>
            <div className="px-2 py-1.5" data-testid="user-menu-mode-switcher">
              <div className="flex rounded-full bg-black/5 p-0.5" role="group" aria-label={t("nav.modeLabel")}>
                <button
                  type="button"
                  data-testid="mode-switch-buyer"
                  aria-pressed={mode === "buyer"}
                  onClick={() => switchMode("buyer")}
                  className={`flex-1 rounded-full text-[11px] font-medium py-1.5 transition-colors duration-200 ${
                    mode === "buyer" ? "bg-white text-[#0A0A0A] shadow-sm" : "text-black/50 hover:text-black"
                  }`}
                >
                  {t("nav.modeBuying")}
                </button>
                <button
                  type="button"
                  data-testid="mode-switch-organizer"
                  aria-pressed={mode === "organizer"}
                  onClick={() => switchMode("organizer")}
                  className={`flex-1 rounded-full text-[11px] font-medium py-1.5 transition-colors duration-200 ${
                    mode === "organizer" ? "bg-white text-[#0A0A0A] shadow-sm" : "text-black/50 hover:text-black"
                  }`}
                >
                  {t("nav.modeOrganizing")}
                </button>
              </div>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {canOrganize && (
          <DropdownMenuItem asChild className="rounded-lg cursor-pointer">
            <Link to="/dashboard" data-testid="user-menu-dashboard">
              <LayoutGrid className="w-4 h-4 mr-2" aria-hidden="true" />
              {t("nav.dashboard")}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild className="rounded-lg cursor-pointer">
          <Link to="/account" data-testid="user-menu-account">
            <UserRound className="w-4 h-4 mr-2" aria-hidden="true" />
            {t("nav.myAccount")}
          </Link>
        </DropdownMenuItem>
        {!canOrganize && (
          <DropdownMenuItem asChild className="rounded-lg cursor-pointer">
            <Link to="/create" data-testid="user-menu-become-organizer">
              <Store className="w-4 h-4 mr-2" aria-hidden="true" />
              {t("nav.becomeOrganizer")}
            </Link>
          </DropdownMenuItem>
        )}
        {user.role === "admin" && (
          <DropdownMenuItem asChild className="rounded-lg cursor-pointer">
            <Link to="/design-system" data-testid="user-menu-design-system">
              <Palette className="w-4 h-4 mr-2" aria-hidden="true" />
              {t("nav.designSystem")}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-logout"
          onClick={handleLogout}
          className="rounded-lg cursor-pointer text-red-700 focus:text-red-700"
        >
          <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
          {t("nav.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const lp = useLocalePath();
  // WS16: en landings de campaña (/lp/*) el CTA del header también arrastra los UTM.
  const { pathname, search } = useLocation();
  const createTo = pathname.startsWith("/lp/") ? `/create${search}` : "/create";
  // C5: menú móvil controlado — cada enlace cierra el menú al navegar
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <header
      data-testid="app-header"
      className="fixed top-0 inset-x-0 z-50 h-11 bg-[rgba(251,251,253,0.72)] backdrop-blur-xl [backdrop-filter:saturate(180%)_blur(20px)]"
    >
      <div className="max-w-[1152px] mx-auto px-4 lg:px-6 h-full flex items-center gap-4">
        {/* 3-zone flex layout: equal flexible side zones keep the nav visually
           centered while GUARANTEEING no overlap at any width (no absolute
           positioning). min-w-fit stops zones collapsing below their content. */}
        <div className="flex-1 basis-0 min-w-fit flex items-center justify-start">
          <Wordmark size="sm" />
        </div>

        {/* The main nav NEVER changes with auth state — access lives in the avatar menu.
           Collapse point: lg (5 items + DE labels don't fit on tablets). */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-8 whitespace-nowrap" aria-label={t("a11y.mainNav")}>
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={lp(link.to)} data-testid={link.testid} className={navLinkClass}>
              {t(link.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1 basis-0 min-w-fit flex items-center justify-end gap-3 whitespace-nowrap">
          <LanguageSwitcher />
          {/* C4: acceso fijo al asistente para usuarios logueados (sin flotante) */}
          {user && (
            <button
              type="button"
              onClick={openActivityCenter}
              aria-label={t("ai.centerTitle")}
              title={t("ai.centerTitle")}
              data-testid="header-activity-center"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-black/5 transition-colors duration-200"
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          {user && <NotificationsBell />}
          {user === undefined ? (
            <div className="h-7 w-20 rounded-full bg-black/5 animate-pulse" aria-hidden="true" />
          ) : (
            <>
              {!user && (
                <Link
                  to="/signin"
                  data-testid="nav-signin"
                  className="hidden sm:inline-flex text-xs text-black/70 hover:text-black transition-colors duration-200"
                >
                  {t("nav.signIn")}
                </Link>
              )}
              <Link
                to={createTo}
                data-testid="nav-create-auction"
                className="hidden sm:inline-flex items-center rounded-full h-7 px-3.5 text-xs bg-[#0A0A0A] text-white hover:bg-[#2b2b2b] transition-colors duration-200"
              >
                {t("nav.createAuction")}
              </Link>
              {user && <UserMenu />}
            </>
          )}

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger
              data-testid="mobile-menu-trigger"
              aria-label={t("nav.openMenu")}
              className="lg:hidden inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-black/5 transition-colors duration-200"
            >
              <Menu className="w-4 h-4" aria-hidden="true" />
            </SheetTrigger>
            {/* C5: scroll vertical con safe areas — todas las opciones accesibles
                en pantallas bajas; cada enlace cierra el menú (closeMenu) */}
            <SheetContent side="right" className="w-72 bg-[rgba(251,251,253,0.98)] backdrop-blur-xl overflow-y-auto">
              <SheetTitle className="sr-only">{t("nav.openMenu")}</SheetTitle>
              <div className="flex flex-col gap-6 pt-12 pb-[max(2rem,env(safe-area-inset-bottom))]">
                {NAV_LINKS.map((link) => (
                  <NavLink
                    key={link.to}
                    to={lp(link.to)}
                    onClick={closeMenu}
                    data-testid={`mobile-${link.testid}`}
                    className="text-lg font-medium text-[#1d1d1f] hover:text-black transition-colors duration-200"
                  >
                    {t(link.key)}
                  </NavLink>
                ))}
                {user ? (
                  <>
                    {["organizer", "admin"].includes(user.role) && (
                      <NavLink
                        to="/dashboard"
                        onClick={closeMenu}
                        data-testid="mobile-nav-dashboard"
                        className="text-lg font-medium text-[#1d1d1f] hover:text-black transition-colors duration-200"
                      >
                        {t("nav.dashboard")}
                      </NavLink>
                    )}
                    <NavLink
                      to="/account"
                      onClick={closeMenu}
                      data-testid="mobile-nav-my-account"
                      className="text-lg font-medium text-[#1d1d1f] hover:text-black transition-colors duration-200"
                    >
                      {t("nav.myAccount")}
                    </NavLink>
                  </>
                ) : (
                  <NavLink
                    to="/signin"
                    onClick={closeMenu}
                    data-testid="mobile-nav-signin"
                    className="text-lg font-medium text-[#1d1d1f] hover:text-black transition-colors duration-200"
                  >
                    {t("nav.signIn")}
                  </NavLink>
                )}
                <Link
                  to={createTo}
                  onClick={closeMenu}
                  data-testid="mobile-nav-create-auction"
                  className="inline-flex items-center justify-center rounded-full bg-[#0A0A0A] text-white text-[17px] px-[22px] py-[11px] hover:bg-[#2b2b2b] transition-colors duration-300 w-full"
                >
                  {t("nav.createAuction")}
                </Link>
                <div className="pt-2 border-t border-black/5 flex items-center" data-testid="mobile-language-switcher">
                  <LanguageSwitcher />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
